import React from 'react';
import { useIntl } from '@kyruus/intl';
import _kebabCase from 'lodash/kebabCase';
import _debounce from 'lodash/debounce';
import { Autocomplete } from '@biome-design-system/ui/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Chip } from '@biome-design-system/ui/Chip';
import { Typography } from '@biome-design-system/ui/Typography';
import { CloseIcon } from '@biome-design-system/icons/CloseIcon';

import type {
  SearchFacetWithAppliedTerms,
  SearchModification
} from 'Src/types';
import { messages } from '../../messages';
import {
  getPlaceholder,
  getFacetTerms,
  getAppliedValues,
  getGroupedOptions,
  getSearchModifications,
  type TypeaheadOption
} from './utils';
import { visuallyHidden } from '@biome-design-system/styles';
import { Box } from '@biome-design-system/ui/Box';

// Matches the Autocomplete's default option filtering so the announced results
// count stays in sync with the options rendered in the listbox.
const filterOptions = createFilterOptions<TypeaheadOption>();

export interface TypeaheadFilterProps {
  labelId: string;
  facet: SearchFacetWithAppliedTerms;
  hideCount: boolean;
  updateSearch: (modifications: SearchModification[]) => void;
}

export const TypeaheadFilter = ({
  facet,
  hideCount,
  labelId,
  updateSearch
}: TypeaheadFilterProps) => {
  const intl = useIntl();
  const facetId = _kebabCase(facet.field);

  const placeholder = getPlaceholder(intl, facet.field);

  // Memoize the derived options so their identity is stable across the
  // re-renders driven by the controlled input below. Rebuilding these arrays on
  // every keystroke confuses the Autocomplete's internal state and drops typed
  // characters.
  const { options, grouped, value, applied } = React.useMemo(() => {
    const facetTerms = getFacetTerms(intl, facet);
    const applied = getAppliedValues(facetTerms);
    const { options, grouped } = getGroupedOptions(intl, facetTerms);
    const value = options.filter((option) => applied.includes(option.value));
    return { options, grouped, value, applied };
  }, [intl, facet]);

  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const onChange = (selectedOptions: TypeaheadOption[]) => {
    const modifications = getSearchModifications(
      selectedOptions.map((option) => option.value),
      applied
    );
    if (modifications.length) {
      updateSearch(modifications);
    }
  };

  const descriptionId = `${facetId}-description`;

  // Announce how many options are available to screen readers while the listbox
  // is open, running the Autocomplete's own filter so the count matches what the
  // listbox actually shows (including the full list on focus with no query).
  const matchCount = filterOptions(options, {
    inputValue,
    getOptionLabel: (option) => option.label
  }).length;
  const resultsStatus = !open
    ? ''
    : matchCount === 0
      ? intl.formatMessage(messages.typeaheadNoResultsStatus)
      : intl.formatMessage(messages.typeaheadResultsStatus, {
          count: matchCount
        });

  // Debounce the announcement so rapid typing doesn't flood the live region
  // with intermediate counts; screen readers only hear the settled result.
  const [announcedStatus, setAnnouncedStatus] = React.useState('');
  const announceStatus = React.useMemo(
    () => _debounce(setAnnouncedStatus, 200),
    []
  );
  React.useEffect(() => {
    announceStatus(resultsStatus);
    return () => announceStatus.cancel();
  }, [resultsStatus, announceStatus]);

  return (
    <>
      <Autocomplete
        helperText={intl.formatMessage(messages.typeaheadDescriptionLabel)}
        data-testid={`select-area-${facetId}`}
        multiple={true}
        disableClearable={true}
        autoHighlight={true}
        size="small"
        fullWidth={true}
        id={facetId}
        options={options}
        value={value}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) =>
          option.value === selected.value
        }
        groupBy={grouped ? (option) => option.group ?? '' : undefined}
        placeholder={placeholder}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        onChange={(_event, selected) => onChange(selected)}
        renderTags={(selectedOptions, getTagProps) =>
          selectedOptions.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                aria-label={intl.formatMessage(
                  messages.typeaheadRemoveFilterAriaLabel,
                  { filterName: option.label }
                )}
                label={option.label}
                size="small"
                deleteIcon={<CloseIcon />}
              />
            );
          })
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              {option.label}
              {!hideCount && option.count ? (
                <Typography ml={0.5}> ({option.count})</Typography>
              ) : null}
            </li>
          );
        }}
        slotProps={{
          formHelperText: {
            style: visuallyHidden,
            id: descriptionId
          },
          htmlInput: {
            'aria-labelledby': labelId,
            'data-testid': facetId,
            autoComplete: 'off',
            // Prevent password managers (1Password, LastPass, Dashlane) from
            // attaching an autofill menu to this filter input, which screen
            // readers otherwise announce as "password menu available".
            'data-1p-ignore': true,
            'data-lpignore': true,
            'data-form-type': 'other'
          }
        }}
      />
      <Box sx={visuallyHidden} role="status" aria-live="polite">
        {announcedStatus}
      </Box>
    </>
  );
};
